import { useCallback } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CachedIcon from '@mui/icons-material/Cached';
import EditIcon from '@mui/icons-material/Edit';
import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  RELATION_CHILD_OF,
  RELATION_PARENT_OF,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import {
  EntityRefLinks,
  catalogApiRef,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import { useAsync } from 'react-use';

import { getThemeTokens } from '../../../theme/tokens';
import { EntityRefChips, type EntityInfo } from '../../common/EntityRefChips';
import { EntityPanelFrame } from './EntityPanelFrame';

const SLACK_CHANNEL_ANNOTATION = 'slack.com/channel';

const buildSlackUrl = (value: string): { href: string; label: string } => {
  if (/^https?:\/\//.test(value)) {
    return { href: value, label: 'Slack' };
  }
  const name = value.replace(/^#/, '');
  return {
    href: `https://meltwater.slack.com/app_redirect?channel=${encodeURIComponent(
      name,
    )}`,
    label: `#${name}`,
  };
};

const SlackIcon = (props: React.ComponentProps<typeof SvgIcon>) => (
  <SvgIcon {...props} viewBox="0 0 127 127">
    <path
      d="M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80zm6.6 0c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z"
      fill="#E01E5A"
    />
    <path
      d="M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47zm0 6.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H13.9C6.6 60.1.7 54.2.7 46.9c0-7.3 5.9-13.2 13.2-13.2H47z"
      fill="#36C5F0"
    />
    <path
      d="M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9zm-6.6 0c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.9 6.5 72.8.6 80.1.6c7.3 0 13.2 5.9 13.2 13.2v33.1z"
      fill="#2EB67D"
    />
    <path
      d="M80.1 99.8c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.8h13.2zm0-6.6c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80.1z"
      fill="#ECB22E"
    />
  </SvgIcon>
);

const ROLE_SECTIONS = [
  { label: 'Team leads', key: 'team-leads' },
  { label: 'Engineering managers', key: 'engineering-managers' },
  { label: 'Product owners', key: 'product-owners' },
];

const GroupField = ({
  children,
  label,
}: {
  children: React.ReactNode;
  label: string;
}) => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  return (
    <Box sx={{ display: 'grid', gap: 0.625, minWidth: 0 }}>
      <Typography
        sx={{
          color: tokens.textMuted,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.06em',
          lineHeight: 1.2,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          alignItems: 'center',
          color: tokens.textPrimary,
          display: 'flex',
          minHeight: 24,
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export const EntityGroupOverviewCard = () => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const { allowed: canRefresh } = useEntityPermission(
    catalogEntityRefreshPermission,
  );

  const { name, description, title, annotations, links, namespace } =
    entity.metadata;
  const profile = (
    entity.spec as { profile?: { displayName?: string; email?: string } }
  ).profile;
  const roles = (entity.spec as Record<string, unknown>).roles as
    | Record<string, string[]>
    | undefined;

  const entityLocation = annotations?.[ANNOTATION_LOCATION];
  const allowRefresh =
    entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:');
  const entityMetadataEditUrl = annotations?.[ANNOTATION_EDIT_URL];

  const displayName = profile?.displayName ?? title ?? name;

  const parentRelations = getEntityRelations(entity, RELATION_CHILD_OF, {
    kind: 'Group',
  });
  const childRelations = getEntityRelations(entity, RELATION_PARENT_OF, {
    kind: 'Group',
  });

  const sections = ROLE_SECTIONS.map(({ label, key }) => ({
    label,
    refs: (roles?.[key] ?? []).map(ref =>
      stringifyEntityRef(
        parseEntityRef(ref, {
          defaultKind: 'User',
          defaultNamespace: 'default',
        }),
      ),
    ),
  }));

  const slackChannel = annotations?.[SLACK_CHANNEL_ANNOTATION]?.trim();
  const slackUrl = slackChannel ? buildSlackUrl(slackChannel) : null;

  const allRefs = [...new Set(sections.flatMap(s => s.refs))];
  const hasRoles = allRefs.length > 0;
  const hasLeftColumn =
    (Boolean(namespace) && namespace !== 'default') ||
    Boolean(profile?.email || slackUrl) ||
    parentRelations.length > 0 ||
    childRelations.length > 0;

  const { value: entityMap = new Map<string, EntityInfo>() } =
    useAsync(async () => {
      if (!allRefs.length) return new Map<string, EntityInfo>();
      const { items } = await catalogApi.getEntitiesByRefs({
        entityRefs: allRefs,
        fields: ['kind', 'metadata', 'spec'],
      });
      const map = new Map<string, EntityInfo>();
      for (let i = 0; i < allRefs.length; i++) {
        const item = items[i];
        if (!item) continue;
        const itemProfile = (item.spec as Record<string, unknown> | undefined)
          ?.profile as Record<string, string> | undefined;
        map.set(allRefs[i], {
          displayName:
            itemProfile?.displayName ??
            item.metadata.title ??
            item.metadata.name,
          picture: itemProfile?.picture,
        });
      }
      return map;
    }, [allRefs.join('|')]);

  const refreshEntity = useCallback(async () => {
    await catalogApi.refreshEntity(stringifyEntityRef(entity));
    alertApi.post({
      message: 'Refresh scheduled',
      severity: 'info',
      display: 'transient',
    });
  }, [catalogApi, alertApi, entity]);

  const headerActions = (
    <>
      {allowRefresh && canRefresh && (
        <IconButton
          aria-label="Schedule entity refresh"
          title="Schedule entity refresh"
          onClick={refreshEntity}
        >
          <CachedIcon />
        </IconButton>
      )}
      {entityMetadataEditUrl ? (
        <a
          href={entityMetadataEditUrl}
          target="_blank"
          rel="noreferrer"
          style={{ color: 'inherit' }}
        >
          <IconButton aria-label="Edit entity" title="Edit entity">
            <EditIcon />
          </IconButton>
        </a>
      ) : (
        <IconButton aria-label="Edit entity" title="Edit entity" disabled>
          <EditIcon />
        </IconButton>
      )}
    </>
  );

  return (
    <EntityPanelFrame title={displayName} action={headerActions}>
      {description && (
        <Typography
          sx={{
            color: tokens.textSecondary,
            fontSize: 16,
            lineHeight: 1.75,
            mb: 2.5,
          }}
        >
          {description}
        </Typography>
      )}

      {(hasLeftColumn || hasRoles) && (
        <Box
          sx={{
            display: 'grid',
            gap: 2.5,
            gridTemplateColumns:
              hasLeftColumn && hasRoles ? { xs: '1fr', md: '1fr 1fr' } : '1fr',
          }}
        >
          {hasLeftColumn && (
            <Stack spacing={2.5}>
              {Boolean(namespace) && namespace !== 'default' && (
                <GroupField label="Entity Ref">
                  <Typography sx={{ color: tokens.textPrimary, fontSize: 14 }}>
                    {stringifyEntityRef(entity)}
                  </Typography>
                </GroupField>
              )}
              {(profile?.email || slackUrl) && (
                <GroupField label="Contact">
                  <Stack spacing={0.75}>
                    {profile?.email && (
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          gap: 0.75,
                        }}
                      >
                        <MailOutlineIcon
                          sx={{
                            color: theme.palette.primary.main,
                            flexShrink: 0,
                            fontSize: 16,
                          }}
                        />
                        <Link
                          href={`mailto:${profile.email}`}
                          sx={{
                            color: theme.palette.primary.main,
                            fontSize: 14,
                            fontWeight: 600,
                            textDecoration: 'none',
                          }}
                        >
                          {profile.email}
                        </Link>
                      </Box>
                    )}
                    {slackUrl && (
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          gap: 0.75,
                        }}
                      >
                        <SlackIcon sx={{ flexShrink: 0, fontSize: 16 }} />
                        <Link
                          href={slackUrl.href}
                          target="_blank"
                          rel="noreferrer"
                          sx={{
                            color: theme.palette.primary.main,
                            fontSize: 14,
                            fontWeight: 600,
                            textDecoration: 'none',
                          }}
                        >
                          {slackUrl.label}
                        </Link>
                      </Box>
                    )}
                  </Stack>
                </GroupField>
              )}
              {parentRelations.length > 0 && (
                <GroupField label="Parent group">
                  <EntityRefLinks
                    entityRefs={parentRelations}
                    defaultKind="Group"
                  />
                </GroupField>
              )}
              {childRelations.length > 0 && (
                <GroupField label="Child groups">
                  <EntityRefLinks
                    entityRefs={childRelations}
                    defaultKind="Group"
                  />
                </GroupField>
              )}
            </Stack>
          )}

          {hasRoles && (
            <Stack spacing={2.5}>
              {sections.map(({ label, refs }) =>
                refs.length ? (
                  <GroupField key={label} label={label}>
                    <EntityRefChips refs={refs} entityMap={entityMap} />
                  </GroupField>
                ) : null,
              )}
            </Stack>
          )}
        </Box>
      )}

      {links?.length ? (
        <Box sx={{ display: 'grid', gap: 0.75, mt: 2.5 }}>
          <Typography
            sx={{
              color: tokens.textMuted,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.06em',
              lineHeight: 1.2,
              textTransform: 'uppercase',
            }}
          >
            Links
          </Typography>
          <Stack spacing={0.5}>
            {links.map(link => (
              <Link
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {link.title ?? link.url}
              </Link>
            ))}
          </Stack>
        </Box>
      ) : null}
    </EntityPanelFrame>
  );
};
